'use strict';

const {
  MAX_LEVEL,
  PLAYER_STORAGE_KEY,
  PENDING_RUN_FINISHES_STORAGE_KEY,
  GAME_CONFIG_STORAGE_KEY,
  DASHBOARD_STORAGE_KEY,
  LEADERBOARD_STORAGE_KEY,
  SETTINGS_STORAGE_KEY,
  TUTORIAL_STORAGE_KEY,
  TOUCHPAD_TUTORIAL_STORAGE_KEY
} = require('../config/constants');

function clampInteger(value, min, max, fallback) {
  const number = Number(value);
  if (!Number.isFinite(number)) return fallback;
  return Math.max(min, Math.min(max, Math.floor(number)));
}

class StorageService {
  constructor(userId) {
    this.userId = String(userId);
  }

  key(base) { return `${base}:${this.userId}`; }

  read(base, fallback) {
    try {
      const raw = localStorage.getItem(this.key(base));
      return raw === null ? fallback : JSON.parse(raw);
    } catch (error) {
      return fallback;
    }
  }

  write(base, value) {
    try { localStorage.setItem(this.key(base), JSON.stringify(value)); } catch (error) { /* optional */ }
    return value;
  }

  defaultProfile() {
    return {
      nickname: '飞行员', highestUnlockedLevel: 1, highestClearedLevel: 0,
      totalRuns: 0, totalXp: 0, currentClearStreak: 0, playerLevel: null
    };
  }

  normalizeProfile(profile) {
    const source = profile || {};
    return {
      nickname: typeof source.nickname === 'string' && source.nickname.trim()
        ? source.nickname.slice(0, 24) : '飞行员',
      highestUnlockedLevel: clampInteger(source.highestUnlockedLevel, 1, MAX_LEVEL, 1),
      highestClearedLevel: clampInteger(source.highestClearedLevel, 0, MAX_LEVEL, 0),
      totalRuns: clampInteger(source.totalRuns, 0, 999999999, 0),
      totalXp: clampInteger(source.totalXp, 0, 999999999, 0),
      currentClearStreak: clampInteger(source.currentClearStreak, 0, 999999999, 0),
      playerLevel: source.playerLevel && typeof source.playerLevel === 'object'
        ? source.playerLevel : null,
      selectedPlaneCosmeticKey: source.selectedPlaneCosmeticKey || null,
      selectedTrailCosmeticKey: source.selectedTrailCosmeticKey || null,
      selectedTitleKey: source.selectedTitleKey || null
    };
  }

  loadProfile() { return this.normalizeProfile(this.read(PLAYER_STORAGE_KEY, null)); }
  saveProfile(profile) { return this.write(PLAYER_STORAGE_KEY, this.normalizeProfile(profile)); }

  mergeProfiles(localProfile, remoteProfile) {
    const local = this.normalizeProfile(localProfile);
    const remote = this.normalizeProfile(remoteProfile);
    return this.normalizeProfile({
      nickname: remote.nickname !== '飞行员' ? remote.nickname : local.nickname,
      highestUnlockedLevel: Math.max(local.highestUnlockedLevel, remote.highestUnlockedLevel),
      highestClearedLevel: Math.max(local.highestClearedLevel, remote.highestClearedLevel),
      totalRuns: Math.max(local.totalRuns, remote.totalRuns),
      totalXp: Math.max(local.totalXp, remote.totalXp),
      currentClearStreak: remote.currentClearStreak,
      playerLevel: remote.playerLevel || local.playerLevel,
      selectedPlaneCosmeticKey: remote.selectedPlaneCosmeticKey || local.selectedPlaneCosmeticKey,
      selectedTrailCosmeticKey: remote.selectedTrailCosmeticKey || local.selectedTrailCosmeticKey,
      selectedTitleKey: remote.selectedTitleKey || local.selectedTitleKey
    });
  }

  loadPendingRunFinishes() {
    const value = this.read(PENDING_RUN_FINISHES_STORAGE_KEY, []);
    if (!Array.isArray(value)) return [];
    return value.filter((item) => item && String(item.userId) === this.userId
      && typeof item.runId === 'string' && item.payload && typeof item.payload === 'object'
      && typeof item.queuedAt === 'string');
  }
  savePendingRunFinishes(value) {
    const safe = Array.isArray(value)
      ? value.filter((item) => item && String(item.userId) === this.userId) : [];
    return this.write(PENDING_RUN_FINISHES_STORAGE_KEY, safe);
  }
  loadGameConfig() { return this.read(GAME_CONFIG_STORAGE_KEY, null); }
  saveGameConfig(value) { return this.write(GAME_CONFIG_STORAGE_KEY, value); }
  loadDashboard() { return this.read(DASHBOARD_STORAGE_KEY, null); }
  saveDashboard(value) { return this.write(DASHBOARD_STORAGE_KEY, value); }
  loadLeaderboard() { return this.read(LEADERBOARD_STORAGE_KEY, null); }
  saveLeaderboard(value) { return this.write(LEADERBOARD_STORAGE_KEY, value); }
  loadSettings() {
    const value = this.read(SETTINGS_STORAGE_KEY, {});
    return {
      feedbackEnabled: value.feedbackEnabled !== false,
      touchSensitivity: ['low', 'medium', 'high'].includes(value.touchSensitivity)
        ? value.touchSensitivity : 'medium'
    };
  }
  saveSettings(settings) {
    return this.write(SETTINGS_STORAGE_KEY, {
      feedbackEnabled: settings.feedbackEnabled !== false,
      touchSensitivity: ['low', 'medium', 'high'].includes(settings.touchSensitivity)
        ? settings.touchSensitivity : 'medium'
    });
  }
  hasSeenTutorial() { return Boolean(this.read(TUTORIAL_STORAGE_KEY, false)); }
  markTutorialSeen() { this.write(TUTORIAL_STORAGE_KEY, true); }
  hasSeenTouchpadTutorial() { return Boolean(this.read(TOUCHPAD_TUTORIAL_STORAGE_KEY, false)); }
  markTouchpadTutorialSeen() { this.write(TOUCHPAD_TUTORIAL_STORAGE_KEY, true); }
}

module.exports = StorageService;
